import { Api } from 'InternalLibrary/api/Api';
import {
  ApiContextValue,
  Atoms,
  callbackFallback,
} from 'InternalLibrary/api/ApiContext';
import { createAppProvider } from './AppProvider';
import {
  createUseApi,
  createUseApiAtom,
  GetUseApi,
  GetUseApiAtom,
} from 'InternalLibrary/api';
import { FetchData } from 'InternalLibrary/api/FetchData';
import { ThemeOverride } from '..';
import { TranslateError } from './ErrorBoundary';
import React from 'react';

export type App<TApi extends Api<any, any>, TConfig> = {
  useApp: () => App<TApi, TConfig>;
  config: TConfig;
  // Api
  fetch: FetchData;
  apiContext: React.Context<ApiContextValue<TApi>>;
  Provider: React.FC<React.PropsWithChildren>;
  useApiAtom: GetUseApiAtom<TApi>;
  useApi: GetUseApi<TApi>;
  atoms: Atoms;
  _Api: TApi;
  _ApiContextValue: ApiContextValue<TApi>;
  // Localization
  translateError: TranslateError<App<TApi, TConfig>>;

  // AppConfig:
  // theme
  theme?: ThemeOverride;
  // Testing
  colaUrl: string;
};

export let cachedApp: App<any, any> | null = null;

export function createApp<TApi extends Api, TConfig>(
  appConfig: Partial<Pick<App<TApi, TConfig>, 'theme'>> &
    Pick<App<TApi, TConfig>, 'fetch' | 'config' | 'translateError' | 'colaUrl'>,
): App<TApi, TConfig> {
  if (cachedApp) {
    return cachedApp;
  }

  type TApp = App<TApi, TConfig>;

  const app: App<TApi, TConfig> = {
    apiContext: React.createContext<TApp['_ApiContextValue']>({
      atoms: {},
      setAtom: callbackFallback,
      removeAtom: callbackFallback,
      getAtom: callbackFallback,
      createFetch: callbackFallback,
    }),
    ...appConfig,
  } as App<TApi, TConfig>;

  app.useApp = function useApp() {
    return cachedApp || app;
  };

  app.useApi = createUseApi(app.fetch);
  app.Provider = createAppProvider(app);
  app.useApiAtom = createUseApiAtom(app);

  cachedApp = app;

  return app;
}
